/**
 * The omnibar: one box to go anywhere and do anything — pages, settings, actions, entities, spaces and items, in one
 * list by section, found by fuzzy matching and ranked with what you pick often and lately first. ⌘K (Ctrl+K off Apple's
 * platforms) or the sidebar's search button opens it; Esc closes it, the focus back where it was. The keyboard does
 * everything: ↑↓ to move, ↵ to go, ⌘↵ (Ctrl+Enter) to open in a new tab.
 *
 * What's typed stays inside it: the app around it doesn't redraw as you type.
 */
import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import type { SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { type Frecency, type ItemLook, parse, record, serialize } from "../lib/frecency.ts";
import { highlight } from "../lib/fuzzy.ts";
import { ignore } from "../lib/ignore.ts";
import {
  allGroups,
  allSources,
  type Command,
  type Context,
  flatten,
  gather,
  isShortcut,
  omnibarOpen,
  type Ranked,
  rank,
  refreshed,
  remembered,
  shortcut,
  snapshot,
} from "../lib/omnibar.ts";
import { peek } from "../lib/peek.ts";
import { isApple, sendKeys } from "../lib/platform.ts";
import { location, navigate, spacePath } from "../lib/router.ts";
import { me } from "../lib/state.ts";
import { store, stored } from "../lib/storage.ts";
import { useId } from "../lib/uid.ts";
import { TypeGlyph } from "../pages/items/icons.tsx";
import { EntityGlyph } from "../ui/EntityGlyph.tsx";
import { SearchIcon } from "../ui/icons.tsx";
import { dismissOnDrag } from "../ui/keyboard.ts";
import { FOCUS } from "../ui/styles.ts";
import { registerBuiltins } from "./sources.tsx";

registerBuiltins();

/** After the last key, this long before searching. */
const SEARCH_DEBOUNCE_MS = 120;

/** Where this browser keeps what you picked, per person. */
const frecencyKey = (): string => `revisa.omnibar.${me.value?.id ?? ""}`;

/** The keys the footer names, as keyboards write them. */
const KEYS = { up: "↑", down: "↓", enter: "↵", look: "⇧↵", esc: "Esc" } as const;

/** The shortcut opens (or closes) the omnibar from anywhere, unless another dialog is open. */
function useShortcut(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isShortcut(e) || e.defaultPrevented) {
        return;
      }
      const other = document.querySelector("dialog[open]:not([data-omnibar])");
      if (other) {
        return;
      }
      e.preventDefault();
      omnibarOpen.value = !omnibarOpen.value;
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
}

/** What searching answered, for what was typed. */
interface Found {
  query: string;
  commands: readonly Command[];
}

/** What the sources that search answer for `query` (debounced; a newer query cancels the request before). */
function useSearch(query: string, ctx: Context): { found: Found; searching: boolean } {
  const [found, setFound] = useState<Found>({ query: "", commands: [] });
  const q = query.trim();
  useEffect(() => {
    const searchers = allSources().filter((s) => s.search);
    if (q === "" || searchers.length === 0) {
      setFound({ query: q, commands: [] });
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      // A source that fails offers nothing (the others still do).
      Promise.allSettled(searchers.map((s) => s.search?.(q, ctx, ctl.signal) ?? []))
        .then((all) => {
          if (!ctl.signal.aborted) {
            setFound({ query: q, commands: all.flatMap((r) => (r.status === "fulfilled" ? r.value : [])) });
          }
        })
        .catch(ignore);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [q, ctx]);
  return { found, searching: q !== "" && found.query !== q };
}

/** A command's name, the characters that matched marked. */
function Marked({ text, positions }: { text: string; positions: readonly number[] }) {
  return (
    <>
      {highlight(text, positions).map((r, i) =>
        r.hit ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: runs of one fixed text.
          <mark key={i} class="bg-transparent font-semibold text-current">
            {r.text}
          </mark>
        ) : (
          r.text
        ),
      )}
    </>
  );
}

/**
 * A command's icon: an item's sort (the item list's: a PDF, an image, an email…), else its own. An item kept before
 * looks were (or not found again yet) is taken for a file, its sort guessed from its name.
 */
function Icon({ command, look }: { command: Command; look: ItemLook | undefined }) {
  const item = look ?? (command.id.startsWith("item:") ? { kind: "file" } : undefined);
  return (
    <span class="flex size-5 shrink-0 items-center justify-center text-zinc-500">
      {item ? <TypeGlyph kind={item.kind} mime={look?.mime} name={command.label} /> : command.icon}
    </span>
  );
}

/** The line beside a command: an item's entity with its icon (as the item list shows it), else its hint. */
function Hint({ command, look, space }: { command: Command; look: ItemLook | undefined; space: SpaceOut | null }) {
  // Its entity: by id, or, for an item kept before looks were, by the name kept as its hint.
  const entity = look?.entity
    ? space?.entities.find((e) => e.id === look.entity)
    : command.id.startsWith("item:") && !look
      ? space?.entities.find((e) => e.name === command.hint)
      : undefined;
  const text = entity?.name ?? command.hint;
  return text ? (
    <span class="flex max-w-[45%] shrink-0 items-center gap-1.5 text-xs text-zinc-500">
      {entity ? <EntityGlyph icon={entity.icon} size={14} empty="dot" /> : null}
      <span class="truncate">{text}</span>
    </span>
  ) : null;
}

/** A key, drawn as one. */
const Kbd = ({ children }: { children: ComponentChildren }) => (
  <kbd class="rounded border border-zinc-300 bg-zinc-50 px-1 font-sans text-[0.7rem] text-zinc-600">{children}</kbd>
);

/** The list and its box: open while it's mounted (a new one each time, empty). */
function Palette({
  space,
  onClose,
}: {
  space: SpaceOut | null;
  /** `then`: run once it's closed and the focus is back where it was (an action on what was focused). */
  onClose: (picked?: boolean, then?: () => void) => void;
}) {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const listId = useId();
  // Where you are and what's on offer there: once the space has loaded (opened before, it comes then), not again as
  // it refreshes (live updates) while you type.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the space's id says which space; a refresh isn't another.
  const ctx = useMemo<Context>(() => ({ space }), [space?.id]);
  const [picks, setPicks] = useState<Frecency>(() => parse(stored(frecencyKey())));
  const [before] = useState(picks);
  const local = useMemo(() => {
    const here = gather(ctx);
    return [...here, ...remembered(before, here, ctx.space?.id)];
  }, [ctx, before]);
  const { found, searching } = useSearch(query, ctx);
  const fresh = found.query === query.trim();
  const sections = useMemo(
    // An answer for what was typed before still offers what matches what's typed now, until the new one comes.
    () =>
      rank({
        query,
        local: fresh ? local : [...local, ...found.commands],
        found: fresh ? found.commands : [],
        picks,
        now: Date.now(),
        groups: allGroups(),
      }),
    [query, local, found, fresh, picks],
  );
  const flat = flatten(sections);
  const active = flat.find((r) => r.command.id === activeId) ?? flat[0];
  const optionId = (r: Ranked) => `${listId}-${flat.indexOf(r)}`;
  useEffect(() => {
    if (active) {
      document.getElementById(optionId(active))?.scrollIntoView?.({ block: "nearest" });
    }
  });
  const titles = new Map(allGroups().map((g) => [g.id, g.title()]));
  // A quick look opens over a space's pages (not your account's).
  const looks = space !== null && spacePath(location.value.pathname)?.space === space.id;
  /** How `r` is shown: its look (an item: kept with it, or with what was picked of it), else its own icon. */
  const lookOf = (c: Command): ItemLook | undefined =>
    c.look ?? (c.peek ? picks[`item:${c.peek}`]?.snap?.look : undefined);

  // What searching finds brings what's kept of it up to date (its look, for its icon among the recent ones).
  useEffect(() => {
    const next = refreshed(picks, found.commands);
    if (next !== picks) {
      setPicks(next);
      store(frecencyKey(), serialize(next));
    }
  }, [found, picks]);

  /** Picks `r`: goes there (`tab`: in a new one), or (`look`) shows it in the side panel when it's an item. */
  const pick = (r: Ranked, mode: "go" | "tab" | "look" = "go") => {
    const c = r.command;
    const next = record(picks, c.id, Date.now(), snapshot(c));
    setPicks(next);
    store(frecencyKey(), serialize(next));
    // An action runs once the list's gone and the focus is back (the page inert under it till then: an editor's
    // command would find no selection to act on).
    if (c.run && !(mode === "look" && c.peek && looks) && !(mode === "tab" && c.href)) {
      onClose(false, c.run);
      return;
    }
    onClose(true);
    if (mode === "look" && c.peek && looks) {
      peek(c.peek, document.querySelector("main") ?? document.body);
    } else if (mode === "tab" && c.href) {
      globalThis.open(c.href, "_blank", "noopener");
    } else if (c.run) {
      c.run();
    } else if (c.href) {
      navigate(c.href);
    }
  };
  const move = (by: number) => {
    if (flat.length === 0) {
      return;
    }
    const i = active ? flat.indexOf(active) : -1;
    setActiveId((flat[(i + by + flat.length) % flat.length] as Ranked).command.id);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.isComposing) {
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      move(e.key === "ArrowDown" ? 1 : -1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active) {
        pick(active, how(e));
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };
  /** What a key press or click with `e`'s modifiers does: a new tab (⌘/Ctrl), the side panel (Shift), or go. */
  const how = (e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean }) => {
    if (e.metaKey || e.ctrlKey) {
      return "tab";
    }
    return e.shiftKey ? "look" : "go";
  };
  const count = flat.length;
  return (
    <div class="flex max-h-[min(36rem,100dvh)] flex-col md:max-h-[70dvh]">
      <div class="flex items-center gap-2 border-b border-zinc-200 px-4">
        <SearchIcon size={18} class="shrink-0 text-zinc-500" />
        <input
          // biome-ignore lint/a11y/noAutofocus: opening it is asking to type.
          autoFocus={true}
          type="text"
          role="combobox"
          aria-expanded={true}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active ? optionId(active) : undefined}
          aria-label={t("omnibar.label")}
          placeholder={t("omnibar.placeholder")}
          autocomplete="off"
          autocapitalize="off"
          spellcheck={false}
          enterkeyhint="go"
          value={query}
          onInput={(e) => {
            setQuery(e.currentTarget.value);
            setActiveId(null);
          }}
          onKeyDown={onKeyDown}
          class="h-12 min-w-0 flex-1 bg-transparent text-base text-zinc-900 outline-none placeholder:text-zinc-400"
        />
        <button
          type="button"
          onClick={() => onClose()}
          class={`shrink-0 rounded px-1 text-sm text-zinc-600 md:hidden ${FOCUS}`}
        >
          {t("common.cancel")}
        </button>
      </div>
      <div
        id={listId}
        role="listbox"
        aria-label={t("omnibar.label")}
        class="overflow-y-auto overscroll-contain p-2"
        {...dismissOnDrag()}
      >
        {sections.map((s) => (
          // biome-ignore lint/a11y/useSemanticElements: a listbox's options grouped (ARIA's combobox pattern), not a form's fieldset.
          <div key={s.group} role="group" aria-labelledby={`${listId}-g-${s.group}`} class="mb-1">
            <div id={`${listId}-g-${s.group}`} class="px-2 pt-2 pb-1 text-xs font-medium text-zinc-500">
              {titles.get(s.group) ?? s.group}
            </div>
            {s.entries.map((r) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox's input takes the keys (aria-activedescendant).
              <div
                key={r.command.id}
                id={optionId(r)}
                role="option"
                tabIndex={-1}
                aria-selected={r === active}
                onMouseDown={(e) => e.preventDefault()}
                onMouseMove={() => r !== active && setActiveId(r.command.id)}
                onClick={(e) => pick(r, how(e))}
                class="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm text-zinc-800 aria-selected:bg-blue-50 aria-selected:text-blue-900"
              >
                <Icon command={r.command} look={lookOf(r.command)} />
                <span class="min-w-0 flex-1 truncate">
                  <Marked text={r.command.label} positions={r.positions} />
                </span>
                <Hint command={r.command} look={lookOf(r.command)} space={space} />
              </div>
            ))}
          </div>
        ))}
        {count === 0 && !searching ? (
          <p class="px-2 py-6 text-center text-sm text-zinc-500">{t("omnibar.empty", { query: query.trim() })}</p>
        ) : null}
        {searching ? <p class="px-2 py-2 text-xs text-zinc-500">{t("omnibar.searching")}</p> : null}
      </div>
      <p class="sr-only" aria-live="polite">
        {searching ? "" : t("omnibar.results", { count })}
      </p>
      <div class="hidden flex-wrap gap-x-4 gap-y-1 border-t border-zinc-200 px-4 py-2 text-xs text-zinc-500 md:flex">
        <span>
          <Kbd>{KEYS.up}</Kbd> <Kbd>{KEYS.down}</Kbd> {t("omnibar.keys.move")}
        </span>
        <span>
          <Kbd>{KEYS.enter}</Kbd> {t("omnibar.keys.open")}
        </span>
        <span>
          <Kbd>{sendKeys()}</Kbd> {t("omnibar.keys.newTab")}
        </span>
        {looks ? (
          <span>
            <Kbd>{KEYS.look}</Kbd> {t("omnibar.keys.peek")}
          </span>
        ) : null}
        <span>
          <Kbd>{KEYS.esc}</Kbd> {t("omnibar.keys.close")}
        </span>
      </div>
    </div>
  );
}

/**
 * The omnibar, over everything: a box near the top of the window (on a phone, a sheet across its top, clear of the
 * keyboard). `space`: where you are (none: your account's pages without one).
 */
export function Omnibar({ space }: { space: SpaceOut | null }) {
  useShortcut();
  const ref = useRef<HTMLDialogElement>(null);
  const from = useRef<Element | null>(null);
  /** What to run once it's closed (an action picked). */
  const then = useRef<(() => void) | null>(null);
  const shown = omnibarOpen.value;
  // At once (not after the next frame): a quick close and open again happen in order.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a signal's value (Biome takes it for a constant).
  useLayoutEffect(() => {
    const d = ref.current;
    if (shown && d && !d.open) {
      from.current = document.activeElement;
      d.showModal();
      d.querySelector("input")?.focus();
    } else if (!shown && d?.open) {
      d.close();
    }
  }, [shown]);
  // Gone with the page (another frame): closed.
  useEffect(
    () => () => {
      omnibarOpen.value = false;
    },
    [],
  );
  /** Closes it; the focus goes back where it was, unless something was picked (Enter would land on it: a button
   * pressed again). */
  const close = (picked = false, after?: () => void) => {
    if (picked) {
      from.current = null;
    }
    then.current = after ?? null;
    omnibarOpen.value = false;
  };
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Esc closes it (the input's, and the dialog's own).
    <dialog
      ref={ref}
      data-omnibar=""
      aria-label={t("omnibar.label")}
      class="m-0 mx-auto mt-[12vh] w-[min(40rem,calc(100vw-2rem))] max-w-none overflow-hidden rounded-xl bg-white p-0 text-left text-zinc-900 shadow-2xl backdrop:bg-scrim max-md:mt-0 max-md:w-full max-md:rounded-t-none max-md:pt-[env(safe-area-inset-top)]"
      onClose={(e) => {
        // The event comes after the closing (queued): open again since, it's about the time before.
        if (e.currentTarget.open) {
          return;
        }
        const after = then.current;
        close();
        (from.current as HTMLElement | null)?.focus?.();
        after?.();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          close(); // a click on the backdrop
        }
      }}
    >
      {shown ? <Palette space={space} onClose={close} /> : null}
    </dialog>
  );
}

/** Opens the omnibar: in the sidebar, a search field's look with its shortcut; `compact`, just its icon. */
export function OmnibarButton({ compact = false, class: cls = "" }: { compact?: boolean; class?: string }) {
  const label = t("omnibar.open");
  const onClick = () => {
    omnibarOpen.value = true;
  };
  return compact ? (
    <button
      type="button"
      aria-label={label}
      aria-keyshortcuts={isApple() ? "Meta+K" : "Control+K"}
      onClick={onClick}
      class={`inline-flex size-9 shrink-0 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-950/5 ${FOCUS} ${cls}`}
    >
      <SearchIcon size={20} />
    </button>
  ) : (
    <button
      type="button"
      aria-keyshortcuts={isApple() ? "Meta+K" : "Control+K"}
      onClick={onClick}
      class={`flex w-full items-center gap-2 rounded-md border border-zinc-300/80 bg-white/70 px-3 py-1.5 text-left text-sm text-zinc-500 shadow-xs hover:bg-white ${FOCUS} ${cls}`}
    >
      <SearchIcon size={16} class="shrink-0" />
      <span class="min-w-0 flex-1 truncate">{label}</span>
      <Kbd>{shortcut()}</Kbd>
    </button>
  );
}
